import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowUp, Edit3, Menu } from 'lucide-react';
import { useThread, useThreads } from '../hooks/useThreads';
import { toaster } from '../components/ui';
import { Doodle } from '../components/paper';
import { ChatStarters } from '../components/workspace/ChatStarters';
import { ConversationTray } from '../components/workspace/ConversationTray';
import { MessageBubble, Thinking } from '../components/workspace/MessageBubble';

const MAX_CHARS = 4000;

/** Round header chip, as on mobile's chat header. */
const Chip: React.FC<{ label: string; onClick: () => void; className?: string; children: React.ReactNode }> = ({
  label,
  onClick,
  className = '',
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={`flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-[rgba(28,27,25,0.06)] text-on-surface transition-transform duration-150 hover:bg-[rgba(28,27,25,0.1)] active:scale-90 ${className}`}
  >
    {children}
  </button>
);

/**
 * The AI Workspace — the web twin of mobile's chat (mobile/app/(app)/(tabs)/workspace.tsx):
 * conversations tray (a rail on wide screens, a drawer below), a chat page with
 * ink user bubbles and highlighter tool pills, and a paper composer.
 */
export const AIWorkspace: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeThreadId = Number(searchParams.get('thread')) || null;
  const location = useLocation();
  const navigate = useNavigate();

  const [input, setInput] = useState('');
  const [trayOpen, setTrayOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const autoSubmittedRef = useRef(false);

  const { threads, createThread, isCreating, deleteThread } = useThreads();
  const { thread, isLoading: isThreadLoading, postMessage, isPosting } = useThread(activeThreadId);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [thread?.messages, isPosting]);

  useEffect(() => {
    if (!trayOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setTrayOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [trayOpen]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  }, [input]);

  const ensureThread = async (): Promise<number | null> => {
    if (activeThreadId) return activeThreadId;
    try {
      const created = await createThread(null);
      setSearchParams({ thread: String(created.id) });
      return created.id;
    } catch {
      toaster.error({ title: 'Could not start a new chat' });
      return null;
    }
  };

  const send = async (text?: string): Promise<void> => {
    const content = (text ?? input).trim();
    if (!content || isPosting) return;
    const id = await ensureThread();
    if (id === null) return;
    setInput('');
    try {
      await postMessage({ id, content });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toaster.error({
        title: "Couldn't reach the assistant",
        description: typeof detail === 'string' ? detail : 'Please try again.',
      });
    }
  };

  useEffect(() => {
    if (autoSubmittedRef.current) return;
    const initialQuery = (location.state as { initialQuery?: string } | null)?.initialQuery;
    if (!initialQuery) return;
    autoSubmittedRef.current = true;
    navigate(location.pathname + location.search, { replace: true, state: null });
    void send(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Like mobile: a new chat is just a blank page — the thread is created on first send.
  const startNew = (): void => {
    setSearchParams({});
    setInput('');
    setTrayOpen(false);
    textareaRef.current?.focus();
  };

  const selectThread = (id: number): void => {
    setSearchParams({ thread: String(id) });
    setTrayOpen(false);
  };

  const removeThread = async (id: number): Promise<void> => {
    try {
      await deleteThread(id);
      if (id === activeThreadId) setSearchParams({});
    } catch {
      toaster.error({ title: 'Could not delete that conversation.' });
    }
  };

  const messages = thread?.messages ?? [];
  const empty = !activeThreadId || (thread && messages.length === 0);
  const title = thread?.title?.trim() || threads.find((t) => t.id === activeThreadId)?.title?.trim() || null;
  const canSend = !isPosting && input.trim().length > 0;

  return (
    <div className="flex h-[calc(100dvh-var(--tab-bar-clearance))] overflow-hidden md:h-[100dvh]">
      {trayOpen && (
        <div
          className="fixed inset-0 z-[55] animate-fade-in bg-[rgba(0,0,0,0.35)] lg:hidden"
          onClick={() => setTrayOpen(false)}
          aria-hidden
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-[60] w-[min(21rem,82vw)] shrink-0 bg-surface transition-[transform,box-shadow] duration-[260ms] ease-[cubic-bezier(0.33,1,0.68,1)] lg:static lg:z-auto lg:w-72 lg:translate-x-0 lg:border-0 lg:border-r lg:border-solid lg:border-outline-variant lg:shadow-none lg:transition-none ${
          trayOpen ? 'translate-x-0 shadow-[4px_0_24px_rgba(28,27,25,0.15)]' : '-translate-x-full'
        }`}
      >
        <ConversationTray
          threads={threads}
          currentThreadId={activeThreadId}
          onSelect={selectThread}
          onNew={startNew}
          onDelete={(id) => void removeThread(id)}
          creating={isCreating}
        />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col bg-surface">
        <header className="flex shrink-0 items-center justify-between gap-3 px-5 pb-2 pt-[calc(0.5rem+var(--mobile-nav-offset))] md:pt-5">
          <Chip label="Conversations" onClick={() => setTrayOpen(true)} className="lg:invisible">
            <Menu size={22} />
          </Chip>
          <div className="min-w-0 flex-1 text-center">
            {title ? (
              <h1 className="pb-heading-3 m-0 truncate">{title}</h1>
            ) : (
              <span className="pb-eyebrow">PaperBrain AI</span>
            )}
          </div>
          <Chip label="New chat" onClick={startNew}>
            <Edit3 size={20} />
          </Chip>
        </header>

        <div ref={listRef} className="scrollbar-thin flex-1 overflow-y-auto overscroll-contain px-5">
          {empty ? (
            <div className="flex min-h-full flex-col items-center justify-center py-8 text-center">
              <Doodle kind="chat" />
              <h2 className="pb-heading-2 m-0 mt-4">What&apos;s on your mind?</h2>
              <p className="m-0 mt-2 max-w-md px-3 font-body text-[0.9375rem] leading-6 text-on-surface-variant">
                I&apos;ve read every page you&apos;ve scanned. Ask me anything, or start with one of these.
              </p>
              <ChatStarters onPick={(t) => void send(t)} disabled={isPosting} />
            </div>
          ) : (
            <div className="mx-auto flex max-w-3xl flex-col py-4">
              {isThreadLoading && <Thinking />}
              {messages.map((m, i) => (
                <MessageBubble
                  key={m.id}
                  message={m}
                  streaming={isPosting && i === messages.length - 1 && m.role === 'assistant'}
                />
              ))}
              {/* The gap between sending and the first assistant_start event. */}
              {isPosting && (messages.length === 0 || messages[messages.length - 1].role === 'user') && <Thinking />}
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
          className="shrink-0 border-0 border-t border-solid border-outline-variant bg-surface px-5 pb-3 pt-3 md:pb-6"
        >
          <div className="mx-auto flex max-w-3xl items-end gap-2">
            <textarea
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, MAX_CHARS))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder="Ask your notebook…"
              aria-label="Message"
              className="max-h-32 min-h-[44px] flex-1 resize-none rounded-[1.4rem] border border-solid border-outline-variant bg-surface-container-lowest px-4 py-2.5 font-body text-[0.9375rem] leading-6 text-on-surface outline-none placeholder:text-on-surface-variant focus:border-accent focus:shadow-[0_0_0_3px_var(--focus-ring)]"
            />
            <button
              type="submit"
              disabled={!canSend}
              aria-label="Send"
              className={`flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full border-none transition-[transform,background] duration-150 active:scale-[0.88] disabled:cursor-default ${
                canSend ? 'bg-primary text-on-primary' : 'bg-surface-container-high text-on-surface-variant'
              }`}
            >
              <ArrowUp size={20} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
